import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { Dataset, DatasetFileTypeOption, FileDeleteResponseDto, FileModel } from '@ca/core/models';
import { MachineLearningService, MessagingService, UserNotificationService } from '@ca/core/services';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, from, Observable } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { MessagingCallbackAction } from 'src/app/core/models/messaging-topic-action.model';
import { CziConverterService } from 'src/app/core/services/czi-converter.service';
import { DatasetVM } from 'src/app/features/ml-annotate/models/dataset-vm.model';
import * as FileUtils from '../../../../utils/file/file-utils';
import { CZI_EXTENSION } from '../../constants/constants';
import { CZI_CONVERSION_COMPLETED, CZI_CONVERSION_REMOVED_FAILED_CONVERSION_FILE } from '../../constants/czi-conversion-messages';
import { DatasetFileRemovalConfirmationDialogComponent } from '../dataset-file-removal-confirmation-dialog/dataset-file-removal-confirmation-dialog.component';

@UntilDestroy()
@Component({
    selector: 'ca-czi-conversion-dialog',
    templateUrl: './czi-conversion-dialog.component.html',
    styleUrls: ['./czi-conversion-dialog.component.scss'],
})
export class CziConversionDialogComponent {
    @Input() initialNotCziFiles: FileModel[] = [];
    @Input() originalSizeMap = new Map<string, number>();
    @Input() overallSizeOfOriginalFiles: number = 0;
    @Input() datasetId: string;
    @Input() messagingCallbackAction?: MessagingCallbackAction<Dataset | DatasetVM>;
    @Input() retryAction?: MessagingCallbackAction<FileModel[]>;
    completedCount: number = 0;
    completedPercent: number = 0;
    completedSize: number = 0;
    failedConversionFiles: FileModel[] = [];
    private processedFilesCount: number = 0;

    constructor(
        private activeModal: NgbActiveModal,
        private cziConverterService: CziConverterService,
        private machineLearningService: MachineLearningService,
        private messagingService: MessagingService,
        private ngbModal: NgbModal,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
    ) {}

    @Input() set files(files: FileModel[]) {
        const leftNotCziFiles = files.filter(f => (f.type ? f.type : FileUtils.getFileExtension(f.token)) !== CZI_EXTENSION);
        this.failedConversionFiles = files.filter(f => f.convertStatus !== 'Success' && f.convertStatus !== 'Active');
        this.completedCount = this.initialNotCziFiles.length - leftNotCziFiles.length;

        this.processedFilesCount = this.completedCount + this.failedConversionFiles.length;
        this.completedSize = this.initialNotCziFiles.reduce((sum: number, f: FileModel): number => {
            const fileNameWithoutExtension = FileUtils.getFileNameWithoutExtension(f.token);
            const foundIndexInLeftFiles = leftNotCziFiles.findIndex(lf => FileUtils.getFileNameWithoutExtension(lf.token) === fileNameWithoutExtension);
            if (foundIndexInLeftFiles === -1) {
                sum += this.originalSizeMap.get(fileNameWithoutExtension)!;
            }

            return sum;
        }, 0);

        this.completedPercent = this.overallSizeOfOriginalFiles != 0 ? (this.completedSize / this.overallSizeOfOriginalFiles) * 100 : 0;
        if (this.completedPercent === 100 && this.completedCount < this.initialNotCziFiles.length) {
            this.completedPercent = 99;
        }
        if (this.isConversionCompleted() && this.messagingCallbackAction) {
            this.messagingService.publish(this.messagingCallbackAction.topicId, { action: CZI_CONVERSION_COMPLETED });
        }
    }

    dismiss(): void {
        this.activeModal.dismiss();
    }

    isConversionCompleted(): boolean {
        return this.completedPercent == 100 || this.processedFilesCount === this.initialNotCziFiles.length;
    }

    next(): void {
        if (this.cziConverterService.areAllFilesAreConvertedOrFailed(this.initialNotCziFiles)) {
            if (this.messagingCallbackAction) {
                this.messagingService.publish(this.messagingCallbackAction.topicId, {
                    action: this.messagingCallbackAction.action,
                    payload: this.messagingCallbackAction.payload,
                });
            }
            this.dismiss();
        }
    }

    removeFile(file: FileModel): void {
        const deleteDialog: NgbModalRef = this.ngbModal.open(DatasetFileRemovalConfirmationDialogComponent, { size: 'md' });
        const deleteDialogComponentInstance: DatasetFileRemovalConfirmationDialogComponent = <DatasetFileRemovalConfirmationDialogComponent>(
            deleteDialog.componentInstance
        );
        deleteDialogComponentInstance.numberOfFiles = 1;
        deleteDialogComponentInstance.datasetFileTypeOption = DatasetFileTypeOption.FILES;

        from(deleteDialog.result)
            .pipe(
                switchMap((): Observable<FileDeleteResponseDto[]> => {
                    this.userNotificationService.showSpinner();
                    return this.machineLearningService.removeFilesFromDataset$(this.datasetId, [file.id], true).pipe(
                        tap((files: FileDeleteResponseDto[]): void => {
                            const filesWithError: FileDeleteResponseDto[] = files.filter(f => f.error != null);
                            let errorMessage: string = '';
                            for (let i = 0; i < filesWithError.length; i++) {
                                errorMessage += filesWithError[i].error + (i < filesWithError.length - 1 ? ', ' : '');
                            }
                            if (errorMessage) {
                                this.userNotificationService.showError(
                                    (this.translateService.instant('MACHINE_LEARNING.SELECT_DATASET.REMOVE_FILES_FROM_DATASET_PARTIAL_SUCCESS', {
                                        errorMessage,
                                    }) as string) || '',
                                );
                            } else {
                                this.failedConversionFiles = this.failedConversionFiles.filter(f => f.token !== file.token);
                                if (this.messagingCallbackAction) {
                                    this.messagingService.publish(this.messagingCallbackAction.topicId, {
                                        action: CZI_CONVERSION_REMOVED_FAILED_CONVERSION_FILE,
                                        payload: file,
                                    });
                                }
                                this.userNotificationService.showSuccess('MY_WORKSPACE.MY_FILES.FILE_DELETED_SUCCESSFULLY');
                            }
                        }),
                        catchError((err: HttpErrorResponse): Observable<never> => {
                            const errorMessage: string = err.error?.description || err.message;
                            this.userNotificationService.showError(errorMessage);
                            return EMPTY;
                        }),
                        finalize((): void => this.userNotificationService.hideSpinner()),
                    );
                }),
                catchError((): Observable<never> => EMPTY),
                untilDestroyed(this),
            )
            .subscribe();
    }

    retry(): void {
        this.dismiss();
        if (this.retryAction) {
            const payload: FileModel[] = this.failedConversionFiles.map(file => ({ ...file, ...{ convertStatus: 'NotCZI', sizeInBytes: 0 } }) as FileModel);
            this.messagingService.publish(this.retryAction.topicId, { action: this.retryAction.action, payload });
        }
    }
}
